/**
 * 영업팀 월간보고서 — 보기·결재 모달 (v2.115.0 · 2026-09-02)
 *
 * ⚠파일 이름 주의 — `SalesReportModal.tsx` 는 **판매보고서(판보)** 가 이미 쓰고 있다.
 *   같은 이름을 쓰면 그쪽(영업건 상세·내 결재함 두 곳에서 여는 창)을 덮어쓰게 되므로
 *   이 문서는 `SalesMonthlyReportModal` 로 둔다.
 *
 * ⭐**숫자는 아무도 못 고친다.** 집계는 서버가 매출 분석과 같은 함수로 만들어 상신 시점에
 *   굳히고(확정본 PDF), 사람이 적는 것은 [기타 특이사항] 한 칸뿐이다. 그래서 이 창에는
 *   숫자 수정 칸이 없다 — 화면만 숨기는 게 아니라 서버도 화면이 보낸 숫자를 받지 않는다.
 *
 * ⭐수정 칸이 열리는 경우는 **하나뿐**이다: 상태가 작성중·반려이고 **내가 작성자일 때**.
 *   (서버도 같은 조건으로만 PUT 을 받는다. 화면 조건은 편의일 뿐 통제는 서버가 한다.)
 *
 * 결재(승인·반려·회수·삭제)는 지출결재와 **같은 API** — 여기서 다시 만들지 않는다.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../lib/expense-api';
import { salesPeriodText, updateSalesReport, type SalesReportBody } from '../lib/sales-report-api';
import { EXPENSE_STATUS_BADGE } from './ExpenseDocModal';
import { signatureImageUrl } from '../lib/approval-api';
import { DocHistoryList } from './DocHistoryList';
import { openServerPdfPreview } from '../lib/pdf-preview';
import { useAuth } from '../lib/auth-context';
import { LoadingLines } from './ui/Skeleton';

interface Props {
  docId: number;
  onClose: () => void;
}

export function SalesMonthlyReportModal({ docId, onClose }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();

  const detail = useQuery({
    queryKey: ['expense', 'doc', docId],
    queryFn: () => api.getExpenseDoc(docId),
  });

  const doc = detail.data?.doc ?? null;
  // 본문(대상 월·특이사항·작성자 표기). ⚠숫자는 여기 없다 — 확정본 PDF 안에만 있다
  const body: SalesReportBody | null = detail.data?.salesReport ?? null;

  /**
   * 특이사항 편집 초안. **불러온 값을 useEffect 로 복사하지 않는다** — 저장 후 다시 불러오면
   * 초안을 null 로 되돌려 서버 값을 그대로 보게 한다(둘이 갈리지 않는다).
   */
  const [draft, setDraft] = useState<string | null>(null);
  const notes = draft ?? body?.notes ?? '';

  // 작성자 본인 + 작성중·반려일 때만 특이사항을 고칠 수 있다 (서버도 같은 조건)
  const canEditNotes =
    !!doc && !!user && doc.authorId === user.userId
    && (doc.status === '작성중' || doc.status === '반려');

  const save = useMutation({
    mutationFn: (submit: boolean) => updateSalesReport(docId, { notes, submit }),
    onSuccess: (_r, submit) => {
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['expense'] });
      if (submit) onClose();
    },
    onError: (e) => window.alert((e as Error).message),
  });

  const act = useMutation({
    mutationFn: (kind: 'approve' | 'reject' | 'recall') => {
      if (kind === 'approve') return api.approveExpenseDoc(docId);
      if (kind === 'recall') {
        // ⭐완결 문서 회수는 사유 필수 (v2.101.0) — 다른 결재 모달과 같은 규칙
        if (doc?.status === '최종승인') {
          const r = window.prompt('완결(최종승인) 문서를 회수합니다.\n사유를 적어 주세요 (필수 — 처리 이력에 남습니다)') ?? '';
          if (!r.trim()) return Promise.reject(new Error('사유가 없으면 완결 문서를 회수할 수 없습니다'));
          return api.recallExpenseDoc(docId, r.trim());
        }
        return api.recallExpenseDoc(docId);
      }
      const reason = window.prompt('반려 사유를 적어 주세요 (선택)\n\n※ 작성자가 특이사항을 고쳐 다시 상신하며, 그때 숫자도 다시 집계됩니다.') ?? '';
      return api.rejectExpenseDoc(docId, reason);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense'] });
      onClose();
    },
    onError: (e) => window.alert((e as Error).message),
  });

  const del = useMutation({
    mutationFn: () => api.deleteExpenseDoc(docId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense'] });
      onClose();
    },
    onError: (e) => window.alert((e as Error).message),
  });

  const busy = save.isPending || act.isPending || del.isPending;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[70] flex items-start justify-center overflow-y-auto p-4"
    >
      <div className="bg-white rounded shadow-xl w-full max-w-3xl my-6">
        {/* 머리 */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <h2 className="ui-modal-title shrink-0">영업팀 월간보고서</h2>
            {doc && <span className="text-xs text-slate-500 font-mono shrink-0">{doc.docNo}</span>}
            {doc && (
              <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${EXPENSE_STATUS_BADGE[doc.status] ?? ''}`}>
                {doc.status}
              </span>
            )}
          </div>
          <button type="button" onClick={onClose} className="ui-close">✕</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {detail.isLoading && <div className="text-sm text-slate-400 py-6 text-center"><LoadingLines /></div>}

          {doc?.rejectReason && (
            <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-3 py-2">
              반려 사유: {doc.rejectReason}
            </div>
          )}

          {/* 보고 개요 */}
          {body && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">보고 개요</div>
              <table className="w-full text-[13px] border border-slate-200 rounded overflow-hidden">
                <tbody>
                  <tr className="border-b border-slate-100">
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2 w-[130px]">대상 월</th>
                    <td className="px-3 py-2">
                      <b>{salesPeriodText(body.periodYm)}</b>
                      <span className="text-slate-500 text-xs ml-1.5">({body.periodYm})</span>
                    </td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2">제목</th>
                    <td className="px-3 py-2">{doc?.purpose ?? ''}</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2">작성자</th>
                    <td className="px-3 py-2">
                      {body.author.name || doc?.authorName}
                      <span className="text-slate-500 text-xs ml-1.5">
                        {body.author.dept} {body.author.rank}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2">작성·수정</th>
                    <td className="px-3 py-2 text-slate-600 text-[13px]">
                      {body.createdAt ? body.createdAt.slice(0, 16) : '—'}
                      {body.updatedAt && body.updatedAt !== body.createdAt && (
                        <span className="text-slate-400 ml-1.5">· 수정 {body.updatedAt.slice(0, 16)}</span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className="text-[12px] text-slate-500 mt-1.5">
                <b>숫자는 상신할 때 굳습니다 — 어디서도 고칠 수 없습니다</b>(서버가 매출 분석과 같은 방식으로 집계).
                사람이 적는 것은 아래 [기타 특이사항]뿐이고, 표와 그래프는 [📄 보고서 보기]의 확정본 PDF 로 봅니다.
              </p>
            </section>
          )}

          {/* 기타 특이사항 — 읽기 전용이 기본, 작성자의 작성중·반려 문서만 편집 */}
          {body && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">
                기타 특이사항
                {canEditNotes && <span className="font-normal text-slate-400 ml-1.5">— 한 줄 = 항목 하나</span>}
              </div>
              {canEditNotes ? (
                <>
                  <textarea
                    value={notes}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={'예)\n[인포트렌드] 6월 여신 송금 $40,206\n[마케팅] 보안박람회 참가 (3.18~20 킨텍스)'}
                    className="ui-input ui-textarea w-full min-h-[140px]"
                  />
                  <div className="flex items-center gap-2 mt-1.5">
                    <button type="button" disabled={busy}
                      onClick={() => save.mutate(false)}
                      className="btn btn-secondary">
                      {save.isPending ? '저장 중…' : '💾 저장'}
                    </button>
                    <button type="button" disabled={busy}
                      onClick={() => {
                        // ⭐다시 상신하면 숫자도 그 시점으로 **다시** 굳는다 — 반려가 대개 「숫자가 이상하다」라서다
                        if (window.confirm('결재로 다시 올립니다.\n\n※ 이때 숫자를 지금 자료로 다시 집계해 굳힙니다.')) {
                          save.mutate(true);
                        }
                      }}
                      className="btn btn-primary">
                      📤 다시 상신
                    </button>
                    <span className="text-xs text-slate-500">특이사항만 고칠 수 있습니다 — 숫자는 고칠 수 없습니다.</span>
                  </div>
                </>
              ) : (
                <div className="text-[13px] whitespace-pre-wrap leading-relaxed border border-slate-200 rounded px-3 py-2 bg-slate-50/60 min-h-[52px]">
                  {body.notes || <span className="text-slate-300">적힌 내용이 없습니다</span>}
                </div>
              )}
            </section>
          )}

          {/*
            결재판 — ⭐이 문서는 **3칸**이다(담당 → 부서장 → 대표이사 · 경영지원 없음).
            그래서 칸 수를 적지 않고 서버가 준 cells 를 그대로 훑는다. 4칸으로 못박으면
            종류가 늘 때마다 어긋난다.
          */}
          {detail.data && detail.data.cells.length > 0 && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">
                결재 <span className="font-normal text-slate-400">— 경영지원 결재는 없습니다</span>
              </div>
              <div className="flex gap-2">
                {detail.data.cells.map((c) => (
                  <div key={c.role} className="border border-slate-300 rounded text-center w-[86px]">
                    <div className="text-[11px] bg-slate-100 py-0.5 text-slate-600">{c.role}</div>
                    <div className="h-9 flex items-center justify-center text-xs">
                      {c.skipped ? (
                        <span className="text-slate-300">생략</span>
                      ) : c.stamped ? (
                        c.userId && c.hasSignature ? (
                          <img src={signatureImageUrl(c.userId)} alt={c.name}
                            className="max-h-[30px] max-w-[70px] object-contain"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        ) : (
                          <span className="font-semibold text-slate-700">{c.name}</span>
                        )
                      ) : (
                        <span className="text-slate-300">대기</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 pb-0.5">
                      {c.skipped ? '' : (c.stampedAt ?? '').slice(2, 10)}
                      {!c.skipped && c.delegated && (
                        <span className="ml-0.5 text-[11px] text-amber-600 font-semibold">(대결)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <DocHistoryList history={detail.data?.history} />
        </div>

        {/* 단추 */}
        <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-3">
          <div className="flex gap-2">
            {detail.data?.attachmentId ? (
              <button type="button"
                onClick={() => openServerPdfPreview({
                  pdfUrl: api.attachmentDownloadUrl(detail.data!.attachmentId!),
                  filename: `${doc?.docNo ?? '영업팀월간보고서'}_영업팀월간보고서.pdf`,
                })}
                className="btn btn-secondary">
                📄 보고서 보기
              </button>
            ) : (
              // 상신 전(작성중)에는 확정본이 아직 없다 — 없는 이유를 적어 둔다
              <span className="text-[12px] text-slate-400 self-center">확정본 PDF 는 상신할 때 만들어집니다.</span>
            )}
            {detail.data?.canDelete && (
              <button type="button" disabled={busy}
                onClick={() => {
                  if (window.confirm(`${doc?.docNo ?? '이 문서'} 를 삭제할까요?\n그 달 보고서를 다시 올릴 수 있게 됩니다.`)) del.mutate();
                }}
                className="btn btn-danger">
                {del.isPending ? '삭제 중…' : '🗑 삭제'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {detail.data?.canRecall && (
              <button type="button" onClick={() => act.mutate('recall')} disabled={busy}
                className="btn btn-secondary">↩ 승인 회수</button>
            )}
            {detail.data?.canReject && (
              <button type="button" onClick={() => act.mutate('reject')} disabled={busy}
                className="btn btn-danger">반려</button>
            )}
            {detail.data?.canApprove && (
              <button type="button" onClick={() => act.mutate('approve')} disabled={busy}
                className="btn btn-success-solid">승인</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
